import { API_URL } from "../config/backend";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, Monitor, Smartphone, Gamepad2, Lock, Unlock, KeyRound, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Crewmate from "../components/Crewmate";
import Navbar from "../components/Navbar";
import PromoBanner from "../components/PromoBanner";

function detectMobile() {
  const ua = navigator.userAgent || "";
  // Яндекс Браузер на ТВ — YaApp_TV или YaBrowser на большом экране
  const isYandexTV = /YaApp_TV|YandexTV/i.test(ua);
  // Явные TV паттерны
  const isTVBrowser = /TV|SmartTV|AndroidTV|SMART-TV|Tizen|WebOS|BRAVIA|HbbTV|AppleTV|FireTV|Roku/i.test(ua);
  // Android на большом экране (ТВ, планшет подключённый к монитору)
  const isAndroidBig = /Android/i.test(ua) && window.innerWidth >= 960;
  // YaBrowser на большом экране
  const isYaBrowserBig = /YaBrowser/i.test(ua) && window.innerWidth >= 960;
  if (isYandexTV || isTVBrowser || isAndroidBig || isYaBrowserBig) return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i.test(ua) || window.innerWidth < 960;
}
function useMobile() {
  const [v, set] = useState(() => detectMobile());
  useEffect(() => { const h = () => set(detectMobile()); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);
  return v;
}

const API = API_URL;

// Games that support custom round count selection
const ROUNDS_SUPPORTED = new Set(["quiz","quiz_cinema","quiz_18","quiz_movies","cars","drawing","voting","truthdare","crocodile","music","reverse_music","fast_music","puzzle_music","emoji_movie","fact_or_fake","quiplash","wheelword","flappy","dictator","two_truths","stroop","fibbage","reaction","lexicon","spyfall"]);
const DEFAULT_ROUNDS = { quiz:5, quiz_cinema:5, quiz_18:5, quiz_movies:5, cars:5, drawing:5, voting:5, truthdare:5, crocodile:5, music:5, reverse_music:5, fast_music:5, puzzle_music:5, emoji_movie:5, fact_or_fake:5, quiplash:5, wheelword:5, flappy:5, dictator:5, two_truths:5, stroop:5, fibbage:5, reaction:5, lexicon:5, spyfall:5 };

// Full game rules shown in waiting lobby
const GAME_RULES = {
  quiz:      "Каждый раунд — вопрос с 4 вариантами. Отвечай быстрее — получай больше очков. Кто угадал — получает до 1000 очков в зависимости от скорости.",
  drawing:   "Один игрок рисует слово, остальные угадывают в чат. Кто угадал первым — получает больше очков. Рисующий тоже получает очки за каждую угадку.",
  voting:    "Вопрос типа «Кто из вас…?» — все голосуют. Кто набрал больше голосов — получает очки. Голосуй стратегически!",
  truthdare: "По очереди каждый выбирает Правду или Действие. Правда — честно отвечаешь на вопрос, Действие — выполняешь задание. Можно пропустить за -25 очков.",
  crocodile: "Один объясняет слово жестами и мимикой — без слов! Остальные угадывают. Кто угадал — +150 очков, объясняющий тоже получает за каждую угадку.",
  music:     "Играет фрагмент песни — выбери правильный вариант из 4. Чем быстрее ответишь — тем больше очков. Успей первым!",
  bumper:    "5 раундов на разных картах: поле, лабиринт, острова, каток, хаос с ботами. Управляй машинкой с телефона, набивай очки урона. Побеждает тот, кто нанёс больше всего урона!",
  durak:     "Классический дурак на 36 картах. Атакуй, отбивайся козырями. Кто первый сбросит все карты — не дурак. Последний оставшийся — ДУРАК!",
  uno:       "Сбрось все карты первым! Используй Skip, Reverse, +2, Wild и +4. Когда остаётся 1 карта — нажми УНО! Забудешь — возьмёшь штраф +2.",
  quiplash:  "Каждый придумывает смешной ответ на фразу — анонимно. Потом все видят ответы и голосуют за лучший. 100 очков за каждый голос в твою пользу + 50 бонус победителю раунда!",
  taprace:   "5 трасс с разными сюрпризами. Тапай как можно быстрее — серии тапов дают множители ×1.5, ×2, ×3. Масло замедляет, нитро ускоряет. Первый до финиша — 500 очков!",
  fibbage:   "Необычный факт с пропуском ___. Придумай правдоподобную ложь — чем убедительнее, тем лучше! Угадал правду — +500. За каждого кто поверил твоей лжи — +300. Никто не угадал — бонус +200 всем лжецам!",
  reaction:  "Жди зелёного сигнала и жми кнопку первым! Красный сигнал — ловушка, не нажимай. Фальстарт — штраф. Чем быстрее нажмёшь на реальный сигнал — тем больше очков.",
  spyfall:   "Все игроки знают одну локацию, кроме шпиона. Задавайте друг другу вопросы, отвечайте убедительно и попытайтесь вычислить шпиона до конца раунда. Шпион побеждает, если не раскроется или угадает локацию.",
};

const GAME_META = {
  quiz:      { emoji: "🧠", bg: "#ff3cac", text: "#fff", crew: "#ff3cac", accentBg: "#3d0028" },
  drawing:   { emoji: "✏️", bg: "#00f5ff", text: "#000", crew: "#00f5ff", accentBg: "#003d45" },
  voting:    { emoji: "🗳️", bg: "#ffe600", text: "#000", crew: "#ffe600", accentBg: "#3d3700" },
  truthdare: { emoji: "🔥", bg: "#ff8c00", text: "#fff", crew: "#ff8c00", accentBg: "#3d2000" },
  crocodile: { emoji: "🐊", bg: "#3dffa0", text: "#000", crew: "#3dffa0", accentBg: "#003d28" },
  music:     { emoji: "🎵", bg: "#9b5de5", text: "#fff", crew: "#9b5de5", accentBg: "#1e0a3d" },
  bumper:    { emoji: "🚗", bg: "#ff3355", text: "#fff", crew: "#ff3355", accentBg: "#3d000f" },
  durak:     { emoji: "🃏", bg: "#00AA55", text: "#fff", crew: "#00AA55", accentBg: "#002d15" },
  uno:       { emoji: "🎴", bg: "#FF6600", text: "#fff", crew: "#FF6600", accentBg: "#3d1800" },
  // ── Quiz variants — add new ones here ──
  quiz_cinema: { emoji: "🎬", bg: "#E040FB", text: "#fff", crew: "#E040FB", accentBg: "#2d0040" },
  quiz_movies: { emoji: "🍿", bg: "#FF6B35", text: "#fff", crew: "#FF6B35", accentBg: "#2d1000" },
  quiz_18:     { emoji: "🔞", bg: "#FF1744", text: "#fff", crew: "#FF1744", accentBg: "#3d0010" },
  quiplash:    { emoji: "💬", bg: "#00DDFF", text: "#000", crew: "#00DDFF", accentBg: "#003d45" },
  taprace:      { emoji: "🏎️", bg: "#ff8c00", text: "#000", crew: "#ff8c00", accentBg: "#2a1500" },
  reverse_music: { emoji: "🔁", bg: "#7b3dd5", text: "#fff", crew: "#7b3dd5", accentBg: "#1a0035" },
  cars:          { emoji: "🏎️", bg: "#FF6B00", text: "#fff", crew: "#FF6B00", accentBg: "#1a0800" },
  fast_music:    { emoji: "⚡", bg: "#FFD600", text: "#000", crew: "#FFD600", accentBg: "#2a2000" },
  puzzle_music:  { emoji: "🧩", bg: "#C77DFF", text: "#fff", crew: "#C77DFF", accentBg: "#1a0035" },
  emoji_movie:   { emoji: "🎬", bg: "#FF6B35", text: "#fff", crew: "#FF6B35", accentBg: "#3d1500" },
  fact_or_fake:  { emoji: "🔍", bg: "#00C9FF", text: "#000", crew: "#00C9FF", accentBg: "#003d50" },
  wheelword:     { emoji: "🎡", bg: "#FF6B6B", text: "#fff", crew: "#FF6B6B", accentBg: "#3d0f0f" },
  flappy:        { emoji: "🐦", bg: "#6BCB77", text: "#000", crew: "#6BCB77", accentBg: "#0a2d10" },
  dictator:      { emoji: "👑", bg: "#FFD700", text: "#000", crew: "#FFD700", accentBg: "#3d2f00" },
  two_truths:    { emoji: "🤥", bg: "#B24BF3", text: "#fff", crew: "#B24BF3", accentBg: "#2a0050" },
  stroop:        { emoji: "👁️", bg: "#A0FF00", text: "#000", crew: "#A0FF00", accentBg: "#1a2d00" },
  sniper:        { emoji: "🎯", bg: "#ff3355", text: "#fff", crew: "#ff3355", accentBg: "#2d0010" },
  simon:         { emoji: "⚡", bg: "#9b5de5", text: "#fff", crew: "#9b5de5", accentBg: "#1a0035" },
  quest:         { emoji: "🔍", bg: "#4a90d9", text: "#fff", crew: "#4a90d9", accentBg: "#001830" },
  fibbage:       { emoji: "🤥", bg: "#B24BF3", text: "#fff", crew: "#B24BF3", accentBg: "#2a0050" },
  jeopardy:      { emoji: "🏆", bg: "#FFD700", text: "#000", crew: "#FFD700", accentBg: "#3d2f00" },
  reaction:      { emoji: "⚡", bg: "#00f5ff", text: "#000", crew: "#00f5ff", accentBg: "#003040" },
  lexicon:     { emoji: "📖", bg: "#4FC3F7", text: "#000", crew: "#4FC3F7", accentBg: "#002d3d" },
  screenplay:    { emoji: "🎬", bg: "#FF6B35", text: "#fff", crew: "#FF6B35", accentBg: "#2d0e00" },
  spyfall:       { emoji: "🕵️", bg: "#7b3dd5", text: "#fff", crew: "#7b3dd5", accentBg: "#1a0035" },
};


// ── GameCard component ───────────────────────────────────────────────────────
function GameCard({ game, idx, creating, openGame, isPro = false, promoVerified = false }) {
  const m = GAME_META[game.id] || GAME_META.quiz;
  const busy = creating === game.id;
  const isGrey = game.disabled === "grey";

  return (
    <button
      className="animate-slide-up"
      style={{
        animationDelay: `${idx * 0.08}s`,
        position: "relative",
        padding: 0,
        margin: 0,
        appearance: "none",
        WebkitAppearance: "none",
        border: "1.5px solid rgba(255,255,255,0.08)",
        borderRadius: "1rem",
        background: "#0d0020",
        textAlign: "left",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        cursor: isGrey ? "not-allowed" : "pointer",
        transition: "transform 0.15s, box-shadow 0.15s",
        verticalAlign: "top",
        ...(isGrey ? { filter:"grayscale(1)", opacity:0.45, pointerEvents:"none" } : {}),
      }}
      onClick={() => !isGrey && openGame(game)}
      disabled={isGrey}
      aria-label={game.name}
      data-testid={`game-card-${game.id}`}
      onMouseEnter={e => { if (!isGrey) { e.currentTarget.style.transform="translateY(-4px)"; e.currentTarget.style.boxShadow=`0 12px 32px ${m.bg}33`; }}}
      onMouseLeave={e => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=""; }}
    >
      {/* PRO / FREE badge */}
      {isPro ? (
        <div style={{
          position:"absolute", top:"0.5rem", right:"0.5rem", zIndex:3,
          background: promoVerified ? "#166534" : "#9b5de5",
          borderRadius:"0.4rem", padding:"0.1rem 0.45rem",
          fontFamily:"'Fredoka One',sans-serif", fontSize:"0.65rem", color:"#fff",
          display:"flex", alignItems:"center", gap:"0.2rem",
        }}>
          {promoVerified
            ? <><Unlock style={{width:"0.6rem", height:"0.6rem"}}/> PRO</>
            : <><Lock style={{width:"0.6rem", height:"0.6rem"}}/> PRO</>
          }
        </div>
      ) : (
        <div style={{
          position:"absolute", top:"0.5rem", right:"0.5rem", zIndex:3,
          background:"rgba(61,255,160,0.15)", border:"1px solid rgba(61,255,160,0.3)",
          borderRadius:"0.4rem", padding:"0.1rem 0.45rem",
          fontFamily:"'Fredoka One',sans-serif", fontSize:"0.65rem", color:"#3dffa0",
        }}>FREE</div>
      )}

      {/* Шапка с картинкой или эмодзи-заглушкой */}
      <div style={{
        position: "relative", height: "100px", overflow: "hidden",
        background: `linear-gradient(135deg, ${m.accentBg}, #0d0020)`,
      }}>
        <span style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "3rem", filter: `drop-shadow(0 0 12px ${m.bg})`,
        }}>
          {m.emoji}
        </span>
        {/* Градиент-переход в фон карточки */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to bottom, transparent 40%, #0d0020)",
        }}/>
        {/* Спиннер поверх картинки */}
        {busy && (
          <div style={{
            position:"absolute", inset:0, display:"flex",
            alignItems:"center", justifyContent:"center",
            background:"rgba(13,0,32,0.6)", zIndex:2,
          }}>
            <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin"/>
          </div>
        )}
      </div>

      {/* Тело карточки */}
      <div style={{ padding: "0.5rem 0.75rem 0.75rem", flex: 1 }}>
        <h2 style={{
          fontFamily:"'Fredoka One',sans-serif", fontSize:"1rem",
          color:"#fff", margin:"0 0 0.2rem", lineHeight:1.2,
        }}>
          {game.name}
        </h2>
        <div style={{
          fontFamily:"'Nunito',sans-serif", fontSize:"0.68rem",
          color: m.bg, marginBottom:"0.3rem", fontWeight:700,
        }}>
          👥 {game.min_players}–{game.max_players} игроков
        </div>
        <p style={{
          fontFamily:"'Nunito',sans-serif", fontSize:"0.72rem",
          color:"#888", lineHeight:1.4, margin:0,
        }}>
          {game.description}
        </p>
      </div>
    </button>
  );
}

const VK_URL = "https://vk.com/neonparty";

function ComingSoon() {
  return (
    <div className="page-root" style={{
      minHeight: "100vh",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "2rem", textAlign: "center",
      overflow: "hidden", position: "relative",
    }}>
      {/* Neon glow blobs */}
      <div style={{ position:"absolute", width:"400px", height:"400px", borderRadius:"50%", background:"#ff3cac18", filter:"blur(80px)", top:"10%", left:"10%", pointerEvents:"none" }} />
      <div style={{ position:"absolute", width:"300px", height:"300px", borderRadius:"50%", background:"#9b5de520", filter:"blur(60px)", bottom:"15%", right:"10%", pointerEvents:"none" }} />
      <div style={{ position:"absolute", width:"250px", height:"250px", borderRadius:"50%", background:"#ffe60015", filter:"blur(50px)", top:"50%", left:"50%", transform:"translate(-50%,-50%)", pointerEvents:"none" }} />

      {/* Logo */}
      <div style={{ marginBottom: "2rem", position: "relative" }}>
        <div style={{
          fontSize: "4rem", marginBottom: "0.5rem",
          filter: "drop-shadow(0 0 20px #ff3cac)",
        }}>🚀</div>
        <h1 style={{
          fontFamily: "'Fredoka One', sans-serif",
          fontSize: "clamp(2.5rem, 8vw, 4.5rem)",
          background: "linear-gradient(135deg, #ff3cac, #ffe600, #00f5ff)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          margin: 0, lineHeight: 1,
          filter: "drop-shadow(0 0 30px #ff3cac88)",
        }}>
          NEONPARTY
        </h1>
        <p style={{
          fontFamily: "'Fredoka One', sans-serif",
          fontSize: "clamp(1rem, 3vw, 1.3rem)",
          color: "#9b5de5", margin: "0.5rem 0 0",
          letterSpacing: "0.15em",
        }}>
          🎮 ВЕЧЕРИНКА НАЧИНАЕТСЯ СКОРО
        </p>
      </div>

      {/* Main message */}
      <div style={{
        background: "#ffffff08",
        border: "1.5px solid #ffffff15",
        borderRadius: "1.5rem",
        padding: "clamp(1.5rem, 4vw, 2.5rem) clamp(1.5rem, 5vw, 3rem)",
        maxWidth: "560px", width: "100%",
        backdropFilter: "blur(10px)",
        marginBottom: "2rem",
        boxShadow: "0 0 40px #ff3cac11",
      }}>
        <p style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: "clamp(1rem, 2.5vw, 1.2rem)",
          color: "#e0e0e0", lineHeight: 1.7,
          margin: "0 0 1.2rem",
        }}>
          Мы готовим для вас <span style={{ color: "#ffe600", fontWeight: 700 }}>лучшую платформу</span> для вечеринок и игровых ночей.
          Телефоны — пульты, один экран — игровое поле.
        </p>
        <p style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: "clamp(0.9rem, 2vw, 1.05rem)",
          color: "#888", lineHeight: 1.6, margin: 0,
        }}>
          Следите за новостями — скоро расскажем о запуске, первых играх и специальных предложениях для ранних пользователей 🎉
        </p>
      </div>

      {/* VK button */}
      <a
        href={VK_URL}
        target="_blank"
        rel="noreferrer"
        style={{
          display: "inline-flex", alignItems: "center", gap: "0.6rem",
          background: "linear-gradient(135deg, #ff3cac, #9b5de5)",
          color: "#fff", textDecoration: "none",
          fontFamily: "'Fredoka One', sans-serif",
          fontSize: "clamp(1rem, 2.5vw, 1.2rem)",
          padding: "0.9rem 2.5rem",
          borderRadius: "3rem",
          boxShadow: "0 0 30px #ff3cac66, 0 4px 20px #0008",
          transition: "transform 0.15s, box-shadow 0.15s",
          letterSpacing: "0.03em",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform="scale(1.05)"; e.currentTarget.style.boxShadow="0 0 45px #ff3cacaa, 0 4px 25px #0008"; }}
        onMouseLeave={e => { e.currentTarget.style.transform="scale(1)"; e.currentTarget.style.boxShadow="0 0 30px #ff3cac66, 0 4px 20px #0008"; }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.07 2H8.93C3.33 2 2 3.33 2 8.93v6.14C2 20.67 3.33 22 8.93 22h6.14C20.67 22 22 20.67 22 15.07V8.93C22 3.33 20.67 2 15.07 2zm3.08 13.5h-1.7c-.64 0-.84-.51-2-1.67-1-.99-1.44-.84-1.44.16v1.51c0 .45-.14.72-1.3.72-1.91 0-4.03-1.16-5.52-3.33C4.5 10.1 4 7.67 4 7.17c0-.27.1-.52.37-.52h1.7c.28 0 .38.12.49.42.54 1.55 1.44 2.91 1.81 2.91.14 0 .2-.06.2-.4V7.91c-.04-.73-.42-.79-.42-1.05 0-.2.17-.4.44-.4h2.67c.23 0 .31.12.31.38v2.69c0 .23.1.31.17.31.14 0 .26-.08.52-.34 1.61-1.8 2.75-4.57 2.75-4.57.08-.2.22-.39.5-.39h1.7c.51 0 .62.26.51.52-.21 1-.23 1.14-2.16 3.66-.17.22-.24.35 0 .6 1.74 1.93 2.14 2.86 2.14 3.13 0 .36-.18.55-.54.55z"/>
        </svg>
        Следить в ВКонтакте
      </a>

      {/* Bottom hint */}
      <p style={{
        fontFamily: "'Nunito', sans-serif",
        fontSize: "0.78rem", color: "#333",
        marginTop: "2.5rem",
      }}>
        NeonParty — вечеринка у вас дома
      </p>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const isMobile = useMobile();
  // На ТВ пропускаем модалку выбора раундов — создаём сразу
  // isTV — только явные TV браузеры, НЕ по ширине экрана
  const isTV = !isMobile &&
    /YaApp_TV|YandexTV|SmartTV|AndroidTV|SMART-TV|Tizen|WebOS|BRAVIA|HbbTV|AppleTV|FireTV|Roku/i.test(navigator.userAgent);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);

  useEffect(() => {
    axios.get(`${API}/games`)
      .then(r => setGames(r.data.games))
      .catch(() => toast.error("Ошибка загрузки"))
      .finally(() => setLoading(false));
  }, []);

  const [pendingGame, setPendingGame] = useState(null); // {id, name} — modal open
  const createBtnRef = useRef(null);
  // На ТВ: ставим фокус с задержкой чтобы OK не закрыл модалку сразу
  useEffect(() => {
    if (pendingGame && createBtnRef.current) {
      const t = setTimeout(() => createBtnRef.current?.focus(), 300);
      return () => clearTimeout(t);
    }
  }, [pendingGame]);
  const [roundCount, setRoundCount] = useState(10);

  // Quest state
  const [showQuestModal, setShowQuestModal] = useState(false);
  const [questList, setQuestList] = useState([]);
  const [selectedQuest, setSelectedQuest] = useState(null);

  // PRO code state
  const PRO_GAME_IDS = new Set([
    "voting", "music", "reverse_music", "fast_music", "puzzle_music", "emoji_movie",
    "fact_or_fake", "bumper", "quiz_cinema", "quiz_18", "quiz_movies", "wheelword",
    "sniper", "quest", "dictator", "stroop", "taprace", "simon", "fibbage",
    "jeopardy", "flappy", "screenplay", "lexicon", "spyfall",
  ]); // must match tier="pro" in /api/games
  const [promoCode, setPromoCode]           = useState(() => localStorage.getItem("neon_promo_code") || "");
  const [promoVerified, setPromoVerified]   = useState(() => !!localStorage.getItem("neon_promo_code"));
  const [promoRoomsLeft, setPromoRoomsLeft] = useState(() => parseInt(localStorage.getItem("neon_promo_rooms") || "0", 10));
  const [promoInput, setPromoInput]         = useState("");
  const [promoChecking, setPromoChecking]   = useState(false);
  const [promoError, setPromoError]         = useState("");
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [pendingProGame, setPendingProGame] = useState(null); // game waiting for code confirm

  const openQuestPicker = () => {
    axios.get(`${API}/quests`).then(r => {
      setQuestList(r.data.quests || []);
      setSelectedQuest(r.data.quests?.[0]?.id || "murder_mansion");
      setShowQuestModal(true);
    }).catch(() => {
      setQuestList([{ id: "murder_mansion", title: "Убийство в особняке", emoji: "🔪" }]);
      setSelectedQuest("murder_mansion");
      setShowQuestModal(true);
    });
  };

  const openGame = (game) => {
    if (creating) return;
    if (PRO_GAME_IDS.has(game.id)) {
      // PRO game — need verified code
      if (promoVerified && promoCode) {
        setPendingProGame(game);
        if (game.id === "quest") {
          openQuestPicker();
        } else if (ROUNDS_SUPPORTED.has(game.id)) {
          if (isTV) {
            createProRoom(game.id, DEFAULT_ROUNDS[game.id] || 10, promoCode);
          } else {
            setPendingGame(game);
            setRoundCount(DEFAULT_ROUNDS[game.id] || 10);
          }
        } else {
          createProRoom(game.id, null, promoCode);
        }
      } else {
        // Need to enter code first
        setPromoError("");
        setPromoInput("");
        setPendingProGame(game);
        setShowPromoModal(true);
      }
      return;
    }
    // Quest — show quest picker
    if (game.id === "quest") {
      axios.get(`${API}/quests`).then(r => {
        setQuestList(r.data.quests || []);
        setSelectedQuest(r.data.quests?.[0]?.id || "murder_mansion");
        setShowQuestModal(true);
      }).catch(() => {
        setQuestList([{ id: "murder_mansion", title: "Убийство в особняке", emoji: "🔪" }]);
        setSelectedQuest("murder_mansion");
        setShowQuestModal(true);
      });
      return;
    }
    if (ROUNDS_SUPPORTED.has(game.id)) {
      if (isTV) {
        // ТВ: создаём сразу с дефолтным числом раундов без модалки
        createRoom(game.id, DEFAULT_ROUNDS[game.id] || 10);
      } else {
        setPendingGame(game);
        setRoundCount(DEFAULT_ROUNDS[game.id] || 10);
      }
    } else {
      createRoom(game.id, null);
    }
  };

  const createRoom = async (gameType, rounds, questId) => {
    if (creating) return;
    setCreating(gameType);
    setPendingGame(null);
    setShowQuestModal(false);
    try {
      const body = { game_type: gameType, host_name: "Host" };
      if (rounds) body.max_rounds = rounds;
      if (gameType === "quest" && questId) body.quest_id = questId;
      const r = await axios.post(`${API}/rooms`, body);
      if (r.data.host_token) sessionStorage.setItem(`host_token_${r.data.room_id}`, r.data.host_token);
      navigate(`/host/${r.data.room_id}`);
    } catch { toast.error("Не удалось создать комнату"); setCreating(null); }
  };

  // Verify promo code and save to localStorage
  const verifyPromoCode = async (code) => {
    setPromoChecking(true);
    setPromoError("");
    try {
      const r = await axios.post(`${API}/promo/verify`, {
        code: code.trim().toUpperCase(),
        game_type: pendingProGame?.id || "voting",
      });
      const { rooms_left } = r.data;
      setPromoCode(code.trim().toUpperCase());
      setPromoVerified(true);
      setPromoRoomsLeft(rooms_left);
      localStorage.setItem("neon_promo_code", code.trim().toUpperCase());
      localStorage.setItem("neon_promo_rooms", String(rooms_left));
      setShowPromoModal(false);
      // Now proceed to game
      if (pendingProGame) {
        if (pendingProGame.id === "quest") {
          openQuestPicker();
        } else if (ROUNDS_SUPPORTED.has(pendingProGame.id)) {
          setPendingGame(pendingProGame);
          setRoundCount(DEFAULT_ROUNDS[pendingProGame.id] || 10);
        } else {
          createProRoom(pendingProGame.id, null, code.trim().toUpperCase());
        }
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Ошибка проверки кода";
      setPromoError(msg);
      if (e.response?.status === 429) {
        // Too many attempts — clear input
        setPromoInput("");
      }
    } finally {
      setPromoChecking(false);
    }
  };

  // Create PRO room — uses and decrements code
  // Create PRO room — code sent in header, server validates & decrements atomically
  const createProRoom = async (gameType, rounds, codeOverride, questId) => {
    if (creating) return;
    setCreating(gameType);
    setPendingGame(null);
    setShowQuestModal(false);
    const codeToUse = codeOverride || promoCode;
    try {
      const body = { game_type: gameType, host_name: "Host" };
      if (rounds) body.max_rounds = rounds;
      if (gameType === "quest" && questId) body.quest_id = questId;
      const r = await axios.post(`${API}/rooms`, body, {
        headers: { "X-Promo-Code": codeToUse },
      });
      // Sync rooms_left from server response
      if (r.data.rooms_left !== undefined) {
        const newLeft = r.data.rooms_left;
        setPromoRoomsLeft(newLeft);
        localStorage.setItem("neon_promo_rooms", String(newLeft));
        if (newLeft <= 0) {
          localStorage.removeItem("neon_promo_code");
          localStorage.removeItem("neon_promo_rooms");
          setPromoCode(""); setPromoVerified(false);
        }
      }
      if (r.data.host_token) sessionStorage.setItem(`host_token_${r.data.room_id}`, r.data.host_token);
      navigate(`/host/${r.data.room_id}`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Не удалось создать комнату";
      if (e.response?.status === 403 || e.response?.status === 429) {
        localStorage.removeItem("neon_promo_code");
        localStorage.removeItem("neon_promo_rooms");
        setPromoCode(""); setPromoVerified(false); setPromoRoomsLeft(0);
        setPromoError(msg);
        setShowPromoModal(true);
        setPendingProGame({ id: gameType });
      } else {
        toast.error(msg);
      }
      setCreating(null);
    }
  };

  /* ── MOBILE LANDING ── */
  if (isMobile) return (
    <div className="page-root" style={{ minHeight: "100vh", color: "#fff" }}>
      <Navbar />
      <div style={{ maxWidth: "480px", margin: "0 auto", padding: "1.5rem 1rem 6rem" }}>

        {/* HERO */}
        <div style={{ textAlign: "center", padding: "2rem 0 1.5rem" }}>
          <div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", marginBottom: "1.25rem" }}>
            <Crewmate color="#ff3cac" size={44} animate="bounce"/>
            <Crewmate color="#ffe600" size={52} animate="bounce"/>
            <Crewmate color="#00f5ff" size={44} animate="bounce"/>
          </div>
          <p style={{ fontFamily: "'Nunito',sans-serif", fontSize: "0.9rem", color: "#bbb", lineHeight: 1.6, marginBottom: "1.5rem" }}>
            Войди в комнату по коду или выбери игру на большом экране
          </p>

          {/* CTA кнопки */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            <button onClick={() => navigate("/join")} style={{
              fontFamily: "'Fredoka One',sans-serif", fontSize: "1.1rem",
              background: "#ff3cac", color: "#fff",
              border: "3px solid #000", borderRadius: "1rem",
              boxShadow: "4px 4px 0 #000", padding: "0.85rem",
              cursor: "pointer", width: "100%",
            }}>
              📱 Войти в комнату
            </button>
            <button onClick={() => navigate("/pro")} style={{
              fontFamily: "'Nunito',sans-serif", fontWeight: 700, fontSize: "0.9rem",
              background: "#9b5de522", color: "#9b5de5",
              border: "1px solid #9b5de544", borderRadius: "0.75rem",
              padding: "0.65rem", cursor: "pointer", width: "100%",
            }}>
              👑 Купить PRO доступ
            </button>
          </div>
        </div>

        {/* ── Розыгрыш промокодов ── */}

        {/* ── Промо-акция ── */}
        <PromoBanner compact />

        {/* КАК ИГРАТЬ */}
        <div style={{ marginBottom: "1rem", padding: "1.25rem", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "1.25rem" }}>
          <h2 style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: "1.1rem", color: "#aaa", marginBottom: "1rem", textAlign: "center" }}>
            КАК ЭТО РАБОТАЕТ
          </h2>
          {[
            { n: "1", color: "#ff3cac", text: "Хост открывает сайт на большом экране и создаёт комнату" },
            { n: "2", color: "#ffe600", text: "Делится 4-буквенным кодом с остальными игроками" },
            { n: "3", color: "#3dffa0", text: "Все подключаются с телефонов — и начинается веселье!" },
          ].map(s => (
            <div key={s.n} style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", marginBottom: "0.75rem" }}>
              <div style={{
                background: s.color, border: "2px solid #000", borderRadius: "0.5rem",
                boxShadow: "2px 2px 0 #000", width: "2rem", height: "2rem", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'Fredoka One', sans-serif", fontSize: "1.1rem", color: "#000",
              }}>{s.n}</div>
              <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: "0.9rem", color: "#ccc", margin: 0, paddingTop: "0.25rem", lineHeight: 1.5 }}>{s.text}</p>
            </div>
          ))}
        </div>

        {/* ИГРЫ */}
        <div style={{ marginBottom: "1rem", padding: "1.25rem", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "1.25rem" }}>
          <h2 style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: "1.1rem", color: "#aaa", marginBottom: "1rem", textAlign: "center" }}>
            25 ИГР НА ЛЮБОЙ ВКУС
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
            {[
              { emoji: "🧠", name: "Викторина", free: true },
              { emoji: "✏️", name: "Рисовалка", free: true },
              { emoji: "🐊", name: "Крокодил", free: true },
              { emoji: "💬", name: "Закончи фразу", free: true },
              { emoji: "🔥", name: "Правда/Действие", free: true },
              { emoji: "🐦", name: "Птичий забег", free: true },
              { emoji: "🤥", name: "2 правды 1 ложь", free: true },
              { emoji: "🎵", name: "Угадай мелодию", free: false },
              { emoji: "🔍", name: "Квест", free: false },
              { emoji: "🚗", name: "Бамперные машинки", free: false },
              { emoji: "🎡", name: "Поле чудес", free: false },
              { emoji: "🎯", name: "Снайпер", free: false },
              { emoji: "🏆", name: "Своя игра", free: false },
              { emoji: "⚡", name: "Реакция", free: true },
            ].map((g, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: "0.4rem",
                background: g.free ? "#001a00" : "#0d0020",
                border: `1.5px solid ${g.free ? "#3dffa044" : "#9b5de544"}`,
                borderRadius: "0.6rem", padding: "0.4rem 0.6rem",
              }}>
                <span style={{ fontSize: "1rem" }}>{g.emoji}</span>
                <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: "0.78rem", color: g.free ? "#3dffa0" : "#aaa" }}>{g.name}</span>
                {!g.free && <span style={{ marginLeft: "auto", fontSize: "0.6rem", color: "#9b5de5", fontFamily: "'Fredoka One', sans-serif" }}>PRO</span>}
              </div>
            ))}
          </div>
          <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: "0.75rem", color: "#444", textAlign: "center", marginTop: "0.75rem" }}>
            🆓 8 бесплатных · 🔒 17 PRO от 99 ₽
          </p>
        </div>

        {/* ДЛЯ КОГО */}
        <div style={{ marginBottom: "1rem", padding: "1.25rem", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "1.25rem" }}>
          <h2 style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: "1.1rem", color: "#aaa", marginBottom: "0.75rem", textAlign: "center" }}>
            ДЛЯ КОГО
          </h2>
          {[
            { emoji: "🏠", text: "Домашние вечеринки и посиделки" },
            { emoji: "🏢", text: "Корпоративы и тимбилдинг" },
            { emoji: "☕", text: "Кафе и рестораны" },
            { emoji: "🎓", text: "Студенческие тусовки" },
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.5rem" }}>
              <span style={{ fontSize: "1.25rem" }}>{item.emoji}</span>
              <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: "0.88rem", color: "#ccc" }}>{item.text}</span>
            </div>
          ))}
        </div>

        {/* ФИНАЛЬНЫЙ CTA */}
        <div style={{ textAlign: "center", paddingTop: "0.5rem" }}>
          <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: "0.85rem", color: "#555", marginBottom: "1rem" }}>
            Уже есть код комнаты?
          </p>
          <button onClick={() => navigate("/join")} className="btn-cartoon btn-success" style={{ padding: "1rem 2rem", fontSize: "1.1rem" }}>
            <Users className="w-5 h-5"/> Войти в комнату
          </button>
        </div>

      </div>
    </div>
  );

  /* ── DESKTOP ── */
  return (
    <div className="min-h-screen bg-cartoon stars-bg relative overflow-hidden">
      <Navbar />



      <div className="relative z-10 px-6 py-10 lg:py-14 max-w-[1800px] mx-auto">

        {/* ── Header ── */}
        <header className="text-center" style={{ marginBottom:"3.5rem", paddingTop:"1rem" }}>
          <h1 className="font-display uppercase leading-none mb-3"
              style={{ fontSize:"clamp(3rem,8vw,6rem)", color:"#fff", textShadow:"4px 4px 0 #ff3cac, 8px 8px 0 #000" }}>
            ВЫБЕРИ <front style={{ fontSize:"clamp(3rem,8vw,6rem)", color:"#ffe600", textShadow:"4px 4px 0 #ff8c00, 8px 8px 0 #000" }}>ИГРУ!</front>
          </h1>
          <p className="font-heading text-xl lg:text-2xl" style={{ color:"#bbb" }}>
            Собери друзей и начни веселье прямо сейчас 🎉
          </p>
        </header>



        {/* ── News + How to play ── */}
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <button
            onClick={() => navigate("/how-to-play")}
            style={{
              fontFamily: "'Fredoka One', sans-serif", fontSize: "1rem",
              background: "#ff3cac22", color: "#ff3cac",
              border: "2px solid #ff3cac55", borderRadius: "0.75rem",
              padding: "0.4rem 1.25rem", cursor: "pointer",
            }}
          >❓ Как играть?</button>
        </div>


        {/* ── Розыгрыш промокодов ── */}

        {/* ── Game cards ── */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 lg:gap-4 mb-14">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="game-card h-40 animate-pulse" style={{ opacity: .4 }}/>
            ))}
          </div>
        ) : (
          <>
            {process.env.REACT_APP_COMING_SOON === "true" ? (
              /* ── Coming Soon заглушка вместо игр ── */
              <div style={{
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                padding: "4rem 2rem", textAlign: "center",
              }}>
                <div style={{ position: "relative", marginBottom: "2rem" }}>
                  <div style={{
                    position: "absolute", inset: "-30px",
                    borderRadius: "50%", background: "#ff3cac18",
                    filter: "blur(30px)",
                  }} />
                  <div style={{ fontSize: "5rem", position: "relative" }}>🚀</div>
                </div>
                <h2 style={{
                  fontFamily: "'Fredoka One', sans-serif",
                  fontSize: "clamp(1.8rem, 5vw, 2.8rem)",
                  background: "linear-gradient(135deg, #ff3cac, #ffe600)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                  margin: "0 0 1rem",
                }}>
                  Скоро открытие!
                </h2>
                <p style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: "clamp(1rem, 2.5vw, 1.15rem)",
                  color: "#aaa", lineHeight: 1.7,
                  maxWidth: "480px", margin: "0 0 0.75rem",
                }}>
                  Мы финишируем последние штрихи и скоро откроем двери для всех.
                  Игры появятся здесь совсем скоро!
                </p>
                <p style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: "0.95rem", color: "#555",
                  margin: "0 0 2rem",
                }}>
                  Следите за новостями — анонс запуска уже готовится 🎉
                </p>
                <a
                  href="https://vk.com/neonparty"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "0.6rem",
                    background: "linear-gradient(135deg, #ff3cac, #9b5de5)",
                    color: "#fff", textDecoration: "none",
                    fontFamily: "'Fredoka One', sans-serif",
                    fontSize: "1.05rem",
                    padding: "0.8rem 2rem",
                    borderRadius: "3rem",
                    boxShadow: "0 0 25px #ff3cac55",
                    transition: "transform 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.07 2H8.93C3.33 2 2 3.33 2 8.93v6.14C2 20.67 3.33 22 8.93 22h6.14C20.67 22 22 20.67 22 15.07V8.93C22 3.33 20.67 2 15.07 2zm3.08 13.5h-1.7c-.64 0-.84-.51-2-1.67-1-.99-1.44-.84-1.44.16v1.51c0 .45-.14.72-1.3.72-1.91 0-4.03-1.16-5.52-3.33C4.5 10.1 4 7.67 4 7.17c0-.27.1-.52.37-.52h1.7c.28 0 .38.12.49.42.54 1.55 1.44 2.91 1.81 2.91.14 0 .2-.06.2-.4V7.91c-.04-.73-.42-.79-.42-1.05 0-.2.17-.4.44-.4h2.67c.23 0 .31.12.31.38v2.69c0 .23.1.31.17.31.14 0 .26-.08.52-.34 1.61-1.8 2.75-4.57 2.75-4.57.08-.2.22-.39.5-.39h1.7c.51 0 .62.26.51.52-.21 1-.23 1.14-2.16 3.66-.17.22-.24.35 0 .6 1.74 1.93 2.14 2.86 2.14 3.13 0 .36-.18.55-.54.55z"/>
                  </svg>
                  Следить в ВКонтакте
                </a>
              </div>
            ) : (
            <>
            {/* ── Промо-акция ── */}
            <PromoBanner compact />

            {/* ── FREE games ── */}
            {games.filter(g => g.tier === "free" && g.disabled !== true).length > 0 && (
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="font-display text-xl text-white uppercase tracking-wider">🆓 Бесплатно</div>
                  <div style={{flex:1, height:"2px", background:"rgba(255,255,255,0.1)", borderRadius:"9999px"}}/>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-4">
                  {games.filter(g => g.tier === "free" && g.disabled !== true).map((game, idx) => (
                    <GameCard key={game.id} game={game} idx={idx} creating={creating} openGame={openGame}/>
                  ))}
                </div>
              </div>
            )}

            {/* ── PRO games ── */}
            {games.filter(g => g.tier !== "free" && g.disabled !== true).length > 0 && (
              <div className="mb-14">
                <div className="flex items-center gap-3 mb-5">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-xl text-white uppercase tracking-wider">🔒 Pro</span>
                    {promoVerified ? (
                      <span className="font-body text-xs px-2 py-0.5 rounded-full"
                        style={{background:"#001a00", border:"1.5px solid #3dffa0", color:"#3dffa0", cursor:"pointer"}}
                        onClick={()=>{setShowPromoModal(true);setPromoInput(promoCode);}}>
                        ✅ Код активен · {promoRoomsLeft === 999999 ? "∞" : promoRoomsLeft} комнат
                      </span>
                    ) : (
                      <span className="font-body text-xs px-2 py-0.5 rounded-full"
                        style={{background:"#1a0040", border:"1.5px solid #9b5de5", color:"#9b5de5"}}>
                        🔑 нужен код доступа
                      </span>
                    )}
                  </div>
                  <div style={{flex:1, height:"2px", background:"rgba(155,93,229,0.2)", borderRadius:"9999px"}}/>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 lg:gap-4">
                  {games.filter(g => g.tier !== "free" && g.disabled !== true).map((game, idx) => (
                    <GameCard key={game.id} game={game} idx={idx} creating={creating} openGame={openGame} isPro promoVerified={promoVerified && promoRoomsLeft > 0}/>
                  ))}
                </div>
                {/* Rooms left counter */}
                {promoVerified && (
                  <div style={{
                    marginTop: "1rem", display: "flex", alignItems: "center",
                    justifyContent: "center", gap: "0.6rem",
                  }}>
                    <div style={{
                      display: "inline-flex", alignItems: "center", gap: "0.5rem",
                      background: "#001a00", border: "2px solid #3dffa055",
                      borderRadius: "0.85rem", padding: "0.45rem 1.1rem",
                      fontFamily: "'Nunito', sans-serif", fontWeight: 700,
                      fontSize: "0.9rem", color: "#3dffa0",
                    }}>
                      🎟️ Осталось комнат: <strong style={{fontSize:"1rem"}}>
                        {promoRoomsLeft === 999999 ? "∞" : promoRoomsLeft}
                      </strong>
                    </div>
                    <button
                      onClick={() => { setShowPromoModal(true); setPromoInput(promoCode); }}
                      style={{
                        background: "none", border: "1.5px solid #ffffff15",
                        borderRadius: "0.6rem", padding: "0.4rem 0.75rem",
                        fontFamily: "'Nunito', sans-serif", fontWeight: 700,
                        fontSize: "0.8rem", color: "#555", cursor: "pointer",
                      }}
                    >сменить код</button>
                  </div>
                )}
              </div>
            )}
            </>
            )}
          </>
        )}

        {/* ── Quest picker modal ── */}
        {showQuestModal && (
          <div style={{
            position:"fixed", inset:0, background:"rgba(0,0,0,0.85)",
            display:"flex", alignItems:"center", justifyContent:"center",
            zIndex:50, padding:"1rem",
          }}>
            <div className="card-cartoon" style={{ width:"100%", maxWidth:"420px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1rem" }}>
                <h2 className="font-display text-white text-2xl">🔍 Выбери квест</h2>
                <button onClick={() => setShowQuestModal(false)} style={{ color:"#555", fontSize:"1.5rem", background:"none", border:"none", cursor:"pointer" }}>✕</button>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:"0.5rem", marginBottom:"1.5rem" }}>
                {questList.map(q => (
                  <button
                    key={q.id}
                    onClick={() => setSelectedQuest(q.id)}
                    style={{
                      background: selectedQuest === q.id ? "#9b5de522" : "#ffffff08",
                      border: `2px solid ${selectedQuest === q.id ? "#9b5de5" : "#ffffff15"}`,
                      borderRadius:"0.75rem", padding:"0.75rem 1rem",
                      textAlign:"left", cursor:"pointer",
                      transition:"all 0.15s",
                    }}
                  >
                    <div style={{ fontFamily:"'Fredoka One', sans-serif", color:"#fff", fontSize:"1rem" }}>
                      {q.emoji} {q.title}
                    </div>
                    {q.description && (
                      <div style={{ fontFamily:"'Nunito', sans-serif", color:"#666", fontSize:"0.78rem", marginTop:"0.2rem" }}>
                        {q.description.length > 80 ? q.description.slice(0,80)+"…" : q.description}
                      </div>
                    )}
                    {q.node_count && (
                      <div style={{ fontFamily:"'Nunito', sans-serif", color:"#444", fontSize:"0.72rem", marginTop:"0.15rem" }}>
                        {q.node_count} сцен
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  const code = localStorage.getItem("neon_promo_code") || undefined;
                  code ? createProRoom("quest", null, code, selectedQuest) : createRoom("quest", null, selectedQuest);
                }}
                disabled={!selectedQuest || !!creating}
                className="btn-cartoon btn-success w-full py-3 text-lg"
              >
                {creating === "quest" ? "Создаём..." : "Начать квест"}
              </button>
            </div>
          </div>
        )}

        {/* ── Round picker modal ── */}
        {pendingGame && (
          <div style={{
            position:"fixed", inset:0, background:"rgba(0,0,0,0.75)",
            display:"flex", alignItems:"center", justifyContent:"center",
            zIndex:1000, padding:"1rem",
          }}>
            <div className="card-cartoon animate-slide-up" style={{maxWidth:"24rem",width:"100%"}}>
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div style={{
                  fontSize:"2rem", background:GAME_META[pendingGame.id]?.bg||"#ff3cac",
                  border:"2.5px solid #000", borderRadius:"0.75rem",
                  boxShadow:"3px 3px 0 #000", width:"3rem", height:"3rem",
                  display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                }}>
                  {GAME_META[pendingGame.id]?.emoji}
                </div>
                <h2 className="font-display text-white text-2xl">{pendingGame.name}</h2>
              </div>
              {/* Rules */}
              <p className="font-body text-[#bbb] text-sm leading-relaxed mb-5">
                {GAME_RULES[pendingGame.id]}
              </p>
              {/* Round picker */}
              <div className="mb-5">
                <p className="font-heading text-[#aaa] text-sm mb-2">Количество раундов</p>
                <div className="flex items-center gap-3 justify-center">
                  <button className="btn-cartoon" style={{background:"#333",padding:"0.5rem 1rem",fontSize:"1.2rem"}}
                    onClick={() => setRoundCount(r => Math.max(3, r - 1))}>−</button>
                  <span className="font-display text-white text-3xl" style={{minWidth:"3rem",textAlign:"center"}}>{roundCount}</span>
                  <button className="btn-cartoon" style={{background:"#333",padding:"0.5rem 1rem",fontSize:"1.2rem"}}
                    onClick={() => setRoundCount(r => Math.min(30, r + 1))}>+</button>
                </div>
                <div className="flex justify-center gap-2 mt-3 flex-wrap">
                  {[5,10,15,20].map(n=>(
                    <button key={n} onClick={()=>setRoundCount(n)}
                      className="btn-cartoon" style={{
                        background: roundCount===n ? GAME_META[pendingGame.id]?.bg||"#ff3cac" : "#222",
                        padding:"0.3rem 0.8rem", fontSize:"0.9rem",
                      }}>{n}</button>
                  ))}
                </div>
              </div>
              {/* Actions */}
              <div className="flex gap-3">
                <button className="btn-cartoon flex-1" style={{background:"#333"}}
                  onClick={() => setPendingGame(null)}>Отмена</button>
                <button className="btn-cartoon flex-1 btn-success" ref={createBtnRef}
                  onClick={() => PRO_GAME_IDS.has(pendingGame.id) ? createProRoom(pendingGame.id, roundCount, localStorage.getItem("neon_promo_code") || undefined) : createRoom(pendingGame.id, roundCount)}>
                  Создать →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Promo code modal ── */}
        {showPromoModal && (
          <div style={{
            position:"fixed", inset:0, background:"rgba(0,0,0,0.82)",
            display:"flex", alignItems:"center", justifyContent:"center",
            zIndex:2000, padding:"1rem",
          }} onClick={()=>setShowPromoModal(false)}>
            <div className="card-cartoon animate-slide-up" style={{maxWidth:"22rem",width:"100%"}}
              onClick={e=>e.stopPropagation()}>
              <h2 className="font-display text-white text-2xl mb-1">🔑 Код доступа PRO</h2>
              <p className="font-body text-[#aaa] text-sm mb-5 leading-relaxed">
                Введи код один раз — он сохранится в браузере.<br/>
                Нет кода? <a href="/pro" onClick={()=>setShowPromoModal(false)} style={{color:"#9b5de5",textDecoration:"underline",cursor:"pointer"}}>Купить PRO →</a>
              </p>
              {/* Input */}
              <input
                type="text"
                value={promoInput}
                onChange={e=>setPromoInput(e.target.value.toUpperCase())}
                onKeyDown={e=>e.key==="Enter"&&promoInput.trim()&&verifyPromoCode(promoInput)}
                placeholder="PRO-XXXX-XXXX"
                maxLength={20}
                autoFocus
                style={{
                  width:"100%", background:"#111", border:"2.5px solid #9b5de5",
                  borderRadius:"0.75rem", color:"#fff", padding:"0.75rem 1rem",
                  fontFamily:"'Nunito',sans-serif", fontWeight:700,
                  fontSize:"1.25rem", letterSpacing:"0.1em", outline:"none",
                  marginBottom:"0.5rem", textAlign:"center",
                  boxShadow:"inset 2px 2px 0 rgba(0,0,0,0.5)",
                }}
              />
              {/* Error */}
              {promoError && (
                <p className="font-body text-[#ff3355] text-sm text-center mb-3 animate-bounce-in">
                  ❌ {promoError}
                </p>
              )}
              {/* Attempts warning */}
              <p className="font-body text-[#555] text-xs text-center mb-4">
                Максимум 10 попыток в час с одного IP
              </p>
              {/* Buttons */}
              <div className="flex gap-3">
                <button className="btn-cartoon flex-1" style={{background:"#333"}}
                  onClick={()=>setShowPromoModal(false)}>Отмена</button>
                <button
                  className="btn-cartoon flex-1"
                  style={{background: promoInput.trim()?"#9b5de5":"#333", opacity: promoChecking?0.6:1}}
                  disabled={!promoInput.trim() || promoChecking}
                  onClick={()=>verifyPromoCode(promoInput)}>
                  {promoChecking ? "Проверка..." : "Активировать"}
                </button>
              </div>
              {/* If already have code — show clear option */}
              {promoVerified && (
                <button className="font-body text-[#555] text-xs w-full mt-3 hover:text-[#ff3355]"
                  style={{background:"none",border:"none",cursor:"pointer"}}
                  onClick={()=>{
                    localStorage.removeItem("neon_promo_code");
                    localStorage.removeItem("neon_promo_rooms");
                    setPromoCode(""); setPromoVerified(false); setPromoRoomsLeft(0);
                    setPromoInput(""); setShowPromoModal(false);
                  }}>
                  Сбросить сохранённый код
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Join section ── */}
        <div className="text-center">
          <div className="inline-block card-cartoon px-10 py-6">
            <div className="flex justify-center gap-3 mb-3">
              <Crewmate color="#9b5de5" size={36} animate="wiggle"/>
              <Crewmate color="#00f5ff" size={36} animate="wiggle"/>
              <Crewmate color="#ff3cac" size={36} animate="wiggle"/>
            </div>
            <p className="font-heading text-xl text-[#aaa] mb-4">Уже есть код комнаты?</p>
            <button onClick={() => navigate("/join")} data-testid="desktop-join-btn"
              className="btn-cartoon btn-secondary px-10 py-4 text-xl">
              <Users className="w-6 h-6"/> Присоединиться
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
